import { msg } from "@lingui/macro";
import type { MessageDescriptor } from "@lingui/core";

/**
 * What a dataset is FOR, chosen when it is created and never edited (PT-155).
 *
 * A work dataset is where the reading is tuned: its runs and its corrections are read to fix it.
 * A reference is where it is measured: built like any dataset, then sealed — its answer key frozen,
 * nothing entering it, read in aggregate only. The backend is the lock (`services.reference_dataset`):
 * these helpers only keep the screens from offering what it refuses.
 */
export type DatasetPurpose = "work" | "reference";

export type DatasetState = "work" | "constituting" | "sealed";

export const STATE_LABELS: Record<Exclude<DatasetState, "work">, MessageDescriptor> = {
  constituting: msg`Référence · en constitution`,
  sealed: msg`Référence · scellée`,
};

/** A dossier in conflict with the rule « one side only », as the API names it. */
export interface DossierConflict {
  tenant_id: string;
  id_patient: number;
  datasets: { id: string; name: string | null }[];
}

export interface ApiRefusal {
  message: string;
  conflicts: DossierConflict[];
}

/**
 * The refusal an API answer carries: FastAPI's `detail`, a sentence or `{message, conflicts}`.
 * `null` when the answer carries none, so the caller falls back to its own sentence.
 */
export function readRefusal(response: unknown): ApiRefusal | null {
  if (!response || typeof response !== "object") return null;
  const { detail, error } = response as { detail?: unknown; error?: unknown };
  if (typeof detail === "string") return { message: detail, conflicts: [] };
  if (detail && typeof detail === "object") {
    const { message, conflicts } = detail as {
      message?: unknown;
      conflicts?: unknown;
    };
    if (typeof message === "string") {
      return {
        message,
        conflicts: Array.isArray(conflicts) ? (conflicts as DossierConflict[]) : [],
      };
    }
  }
  if (typeof error === "string" && error) return { message: error, conflicts: [] };
  return null;
}

/** A score interval as the API stores it, `[low, high]`, else `null`. */
export function asInterval(value: unknown): [number, number] | null {
  if (
    Array.isArray(value) &&
    value.length === 2 &&
    typeof value[0] === "number" &&
    typeof value[1] === "number"
  ) {
    return [value[0], value[1]];
  }
  return null;
}

/** The half-width of an interval, in percentage points, for « ± 2,4 ». */
export function halfWidthPoints(interval: [number, number]): number {
  return ((interval[1] - interval[0]) / 2) * 100;
}

/** What a passage records of the work experiment it confirms, frozen at the time of the passage. */
export interface PassageCandidate {
  experiment_id: string;
  experiment_name: string | null;
  dataset_id: string;
  dataset_name: string | null;
  macro_f1: number | null;
  macro_f1_ci: [number, number] | null;
  exact_match_rate: number | null;
  n_scored_fields: number | null;
  llm_model: string | null;
  completed_at?: string | null;
}

/** The passage's macro F1 minus its candidate's, and whether that exceeds their combined uncertainty. */
export interface ReferenceGap {
  work_macro_f1: number;
  gap: number;
  half_width: number | null;
  beyond_uncertainty: boolean | null;
}

export const CANDIDATE_META_KEY = "reference_candidate";

/** A score as the laboratory prints it — "88.1%" — the way `fmtPct` does on the metrics panel. */
export function fmtScore(value: number | null | undefined): string {
  if (value === null || value === undefined) return "—";
  return `${(value * 100).toFixed(1)}%`;
}

/** An interval's half-width as « ± 2.4 », in percentage points. */
export function fmtHalfWidth(interval: [number, number] | null): string | null {
  return interval ? `± ${halfWidthPoints(interval).toFixed(1)}` : null;
}

/** A gap in percentage points, signed: « −4.9 pts », « +0.6 pts ». */
export function fmtGapPoints(gap: number): string {
  const points = gap * 100;
  const sign = points > 0 ? "+" : points < 0 ? "−" : "";
  return `${sign}${Math.abs(points).toFixed(1)} pts`;
}
